import React, { useState } from 'react';
import { Formik, Form, Field, ErrorMessage } from 'formik';
import * as Yup from 'yup';

/**
 * Contact form — submits to Formspree.
 *
 * Setup (one-time, free):
 *   1. Go to https://formspree.io and create a free account.
 *   2. Create a new form and copy your endpoint URL
 *      (looks like https://formspree.io/f/xxxxxxxx).
 *   3. Add it to a .env file in the project root:
 *        REACT_APP_FORMSPREE_URL=https://formspree.io/f/xxxxxxxx
 *   4. Restart the dev server.
 */
const FORMSPREE_URL = process.env.REACT_APP_FORMSPREE_URL;

const validationSchema = Yup.object({
  name: Yup.string().required('Name is required'),
  email: Yup.string().email('Invalid email address').required('Email is required'),
  message: Yup.string().required('Message is required'),
});

function ContactForm() {
  const [submitStatus, setSubmitStatus] = useState(null); // 'success' | 'error' | null

  const handleSubmit = async (values, { resetForm, setSubmitting }) => {
    if (!FORMSPREE_URL) {
      console.warn(
        'REACT_APP_FORMSPREE_URL is not set. See ContactForm.js for setup instructions.'
      );
      setSubmitStatus('error');
      setSubmitting(false);
      return;
    }

    try {
      const response = await fetch(FORMSPREE_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(values),
      });

      if (response.ok) {
        setSubmitStatus('success');
        resetForm();
      } else {
        setSubmitStatus('error');
      }
    } catch {
      setSubmitStatus('error');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Formik
      initialValues={{ name: '', email: '', message: '' }}
      validationSchema={validationSchema}
      onSubmit={handleSubmit}
    >
      {({ isSubmitting }) => (
        <Form>
          {submitStatus === 'success' && (
            <p className="form-success">
              Thanks! Your message was sent — I'll be in touch soon.
            </p>
          )}
          {submitStatus === 'error' && (
            <p className="form-error">
              Something went wrong. Please try again or email me directly.
            </p>
          )}

          <div>
            <label htmlFor="name">Name</label>
            <Field type="text" id="name" name="name" placeholder="Your name" />
            <ErrorMessage name="name" component="div" className="error" />
          </div>

          <div>
            <label htmlFor="email">Email</label>
            <Field type="email" id="email" name="email" placeholder="your@email.com" />
            <ErrorMessage name="email" component="div" className="error" />
          </div>

          <div>
            <label htmlFor="message">Message</label>
            <Field
              as="textarea"
              id="message"
              name="message"
              rows={5}
              placeholder="Tell me about your project..."
            />
            <ErrorMessage name="message" component="div" className="error" />
          </div>

          <button type="submit" disabled={isSubmitting}>
            {isSubmitting ? 'Sending…' : 'Send Message'}
          </button>
        </Form>
      )}
    </Formik>
  );
}

export default ContactForm;
